import { createApiClient, type ApiClient, type ClientKind, type TokenStore } from "@brandex/api-client";
import { translate, type Locale, type MessageKey } from "@brandex/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Platform capabilities that differ between the browser and the Tauri shell.
 * Shared code never imports Tauri APIs directly (spec §9.2).
 */
export interface Platform {
  kind: "web" | "desktop";
  client: ClientKind;
  tokens: TokenStore;
  notify: (title: string, body: string) => Promise<void>;
  /** Opens an http(s) URL outside the app: a new tab, or the OS browser. */
  openExternal: (url: string) => Promise<void>;
}

/** Router integration, injected by the host app (Next.js in web and desktop). */
export interface LinkProps {
  href: string;
  className?: string;
  children: ReactNode;
  "aria-current"?: "page";
}

export interface Navigation {
  pathname: string;
  navigate: (href: string) => void;
  Link: (props: LinkProps) => ReactNode;
}

interface BrandexContextValue {
  api: ApiClient;
  platform: Platform;
  nav: Navigation;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Public web origin, e.g. https://brandexbd.com (doctor sites live under it). */
  siteUrl: string;
  /** API origin; media URLs from the API are paths on it. */
  apiBaseUrl: string;
  /** Staff admin panel origin; staff who sign in here are sent there. */
  adminUrl: string | undefined;
}

const BrandexContext = createContext<BrandexContextValue | null>(null);

export interface BrandexProviderProps {
  apiBaseUrl: string;
  platform: Platform;
  nav: Navigation;
  initialLocale: Locale;
  onLocaleChange?: (locale: Locale) => void;
  /** Public web origin; defaults to the current origin. */
  siteUrl?: string;
  /** Staff admin panel origin (web and desktop). */
  adminUrl?: string;
  children: ReactNode;
  /** For tests: inject a pre-built client. */
  api?: ApiClient;
}

/** Root provider for dashboard features: API client, server state, i18n. */
export function BrandexProvider(props: BrandexProviderProps) {
  const { apiBaseUrl, platform, nav, initialLocale, onLocaleChange, children } = props;
  const siteUrl = props.siteUrl ?? (typeof window === "undefined" ? "" : window.location.origin);
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: (count, error) => count < 2 && !isClientError(error) },
          mutations: { retry: false },
        },
      }),
  );
  const [api] = useState(
    () =>
      props.api ??
      createApiClient({
        baseUrl: apiBaseUrl,
        client: platform.client,
        tokens: platform.tokens,
        onSessionExpired: () => {
          queryClient.clear();
          nav.navigate("/auth/login");
        },
      }),
  );
  const value = useMemo<BrandexContextValue>(
    () => ({
      api,
      platform,
      nav,
      locale,
      setLocale: (l) => {
        setLocaleState(l);
        onLocaleChange?.(l);
      },
      t: (key, vars) => translate(locale, key, vars),
      siteUrl,
      apiBaseUrl,
      adminUrl: props.adminUrl,
    }),
    [api, platform, nav, locale, onLocaleChange, siteUrl, apiBaseUrl, props.adminUrl],
  );
  return (
    <QueryClientProvider client={queryClient}>
      <BrandexContext.Provider value={value}>{children}</BrandexContext.Provider>
    </QueryClientProvider>
  );
}

function isClientError(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error &&
    typeof error.status === "number" && error.status >= 400 && error.status < 500;
}

export function useBrandex(): BrandexContextValue {
  const ctx = useContext(BrandexContext);
  if (ctx === null) throw new Error("useBrandex must be used inside <BrandexProvider>");
  return ctx;
}

/** Translation shortcut. */
export function useT() {
  return useBrandex().t;
}
